import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { INDUSTRIES } from '@/constants/industries';
import WorkShiftsField from '@/components/WorkShiftsField';
import {
    Search, Plus, MapPin, Briefcase, ChevronRight, Grid3x3, List,
    X, Loader2, MapPinned, Mail, Phone, User, Building2,
    AlertTriangle, Trash2,
} from 'lucide-react';

const VIOLET = '#7C6BEF';
const AVATAR_COLORS = ['#FCD34D', '#F87171', '#A78BFA', '#34D399', '#60A5FA', '#FB923C', '#F472B6', '#22D3EE'];

function avatarColor(name) {
    if (!name) return AVATAR_COLORS[0];
    const idx = name.charCodeAt(0) % AVATAR_COLORS.length;
    return AVATAR_COLORS[idx];
}

export default function AdminClients() {
    const [users, setUsers] = useState([]);
    const [view, setView] = useState('grid');
    const [search, setSearch] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(true);
    // Iter 43 · toggle to include soft-deleted clients/providers in the list.
    const [showDeleted, setShowDeleted] = useState(false);
    // Iter 44 · pending hard-delete confirmation (only for already-soft-deleted users).
    const [pendingHardDelete, setPendingHardDelete] = useState(null);
    // Iter 45 · when opened via `/admin/clients?openNew=1&fromContact={cid}`,
    // preload the New Client modal with the prospect's onboarding_data.
    const [prefillFromContact, setPrefillFromContact] = useState(null);

    const load = () => api.get(`/admin/users${showDeleted ? '?include_deleted=true' : ''}`).then((r) => {
        setUsers((r.data || []).filter((u) => ['client', 'provider'].includes(u.role)));
    }).finally(() => setLoading(false));

    useEffect(() => { load(); }, [showDeleted]);

    // Iter 45 · read the ?openNew=1&fromContact=... shortcut. Fetches the
    // contact and pre-fills the New Client modal. URL params are stripped
    // after processing so a refresh won't re-trigger.
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const p = new URLSearchParams(window.location.search);
        if (p.get('openNew') !== '1') return;
        const contactId = p.get('fromContact');
        if (contactId) {
            api.get(`/admin/contacts/${contactId}`).then((r) => {
                const c = r.data || {};
                const d = c.onboarding_data || {};
                const industry = c.industry || d.industry || '';
                const initial = {
                    name: c.name || d.contact_person || '',
                    email: c.email || d.email || '',
                    phone: c.phone || d.phone || '',
                    industry,
                    company: d.trade_name || d.legal_name || '',
                    trade_name: d.trade_name || '',
                    rfc: d.rfc || '',
                    address: d.address || '',
                    fiscal_address: d.fiscal_address || '',
                    legal_representative: d.legal_representative || '',
                    general_manager: d.general_manager || '',
                    contact_person: d.contact_person || c.name || '',
                    notes: c.message ? `Convertido desde contacto: ${c.message.slice(0, 200)}` : '',
                };
                // Copy any other keys 1:1 that match our form shape.
                for (const k of ['city','state','country','latitude','longitude','property_status','work_shifts']) {
                    if (d[k] !== undefined && d[k] !== '') initial[k] = d[k];
                }
                setPrefillFromContact({ contactId, initialData: initial });
                setShowModal(true);
            }).catch(() => toast.error('No se pudo cargar los datos del prospecto'));
        } else {
            setShowModal(true);
        }
        const url = new URL(window.location.href);
        url.searchParams.delete('openNew');
        url.searchParams.delete('fromContact');
        window.history.replaceState({}, '', url.toString());
    }, []);

    const handleModalCreated = () => {
        setPrefillFromContact(null);
        load();
    };

    const handleRestore = async (u) => {
        try {
            await api.post(`/admin/users/${u.id}/restore`);
            toast.success(`${u.role === 'provider' ? 'Proveedor' : 'Cliente'} restaurado`);
            load();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Error al restaurar'));
        }
    };

    const filtered = users.filter((u) => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (u.company || '').toLowerCase().includes(q) ||
            (u.name || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q) ||
            (u.rfc || '').toLowerCase().includes(q) ||
            (u.industry || '').toLowerCase().includes(q) ||
            (u.city || '').toLowerCase().includes(q);
    });

    return (
        <AdminLayout title="Clientes">
            <div className="flex items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-2 px-4 py-3 flex-1 max-w-xl" style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}>
                    <Search className="w-4 h-4 text-gray-400" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Buscar clientes..."
                        data-testid="clients-search"
                        className="flex-1 bg-transparent border-0 outline-none text-sm p-0"
                    />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <label className="inline-flex items-center gap-2 text-xs font-bold cursor-pointer select-none"
                        title="Mostrar clientes/proveedores eliminados (soft-delete)">
                        <input type="checkbox" checked={showDeleted}
                            onChange={(e) => setShowDeleted(e.target.checked)}
                            data-testid="toggle-show-deleted-clients"
                            className="w-4 h-4" />
                        Ver eliminados
                    </label>
                    <div className="flex border" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                        <button onClick={() => setView('grid')} data-testid="view-grid"
                            className="p-2.5" style={view === 'grid' ? { background: VIOLET, color: 'white' } : {}}>
                            <Grid3x3 className="w-4 h-4" />
                        </button>
                        <button onClick={() => setView('list')} data-testid="view-list"
                            className="p-2.5" style={view === 'list' ? { background: VIOLET, color: 'white' } : {}}>
                            <List className="w-4 h-4" />
                        </button>
                    </div>
                    <button onClick={() => setShowModal(true)} data-testid="new-client-btn"
                        className="text-white px-5 py-3 font-bold inline-flex items-center gap-2 hover:opacity-90"
                        style={{ background: VIOLET }}>
                        <Plus className="w-4 h-4" /> Nuevo Cliente
                    </button>
                </div>
            </div>

            <div className="text-sm text-gray-500 mb-5">
                <span className="font-bold" style={{ color: 'var(--foreground)' }}>{filtered.length}</span> clientes encontrados
            </div>

            {loading ? (
                <div className="p-12 text-center text-gray-500">Cargando...</div>
            ) : filtered.length === 0 ? (
                <div className="p-16 text-center text-gray-500 border" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                    Sin clientes aún. Crea el primero.
                </div>
            ) : view === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {filtered.map((u) => <ClientCard key={u.id} u={u} onRestore={handleRestore} onAskHardDelete={setPendingHardDelete} />)}
                </div>
            ) : (
                <ClientList items={filtered} onRestore={handleRestore} onAskHardDelete={setPendingHardDelete} />
            )}

            {showModal && <NewClientModal
                onClose={() => { setShowModal(false); setPrefillFromContact(null); }}
                onCreated={handleModalCreated}
                initialData={prefillFromContact?.initialData}
            />}
            {pendingHardDelete && (
                <ConfirmHardDeleteClientModal
                    user={pendingHardDelete}
                    onClose={() => setPendingHardDelete(null)}
                    onConfirmed={() => { setPendingHardDelete(null); load(); }}
                />
            )}
        </AdminLayout>
    );
}

function ClientCard({ u, onRestore, onAskHardDelete }) {
    const initial = (u.company || u.name || 'C').charAt(0).toUpperCase();
    const year = u.created_at ? new Date(u.created_at).getFullYear() : '';
    const color = avatarColor(u.company || u.name);
    const isDeleted = !!u.deleted;
    const logoUrl = u.logo_url ? `${process.env.REACT_APP_BACKEND_URL}${u.logo_url}` : null;
    return (
        <div className={`relative ${isDeleted ? 'opacity-60' : ''}`}>
            <Link to={`/admin/clients/${u.id}`} data-testid={`client-card-${u.id}`}
                className="block p-6 transition-transform hover:-translate-y-0.5" style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}>
                <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                        {logoUrl ? (
                            <img src={logoUrl} alt={u.company || u.name}
                                className="w-12 h-12 object-contain bg-white border shrink-0"
                                style={{ borderColor: 'var(--border)' }}
                                data-testid={`client-card-logo-${u.id}`} />
                        ) : (
                            <div className="w-12 h-12 flex items-center justify-center font-display font-black text-lg shrink-0" style={{ background: `${color}20`, color }}
                                data-testid={`client-card-avatar-${u.id}`}>
                                {initial}
                            </div>
                        )}
                        <div className="min-w-0">
                            <div className="font-display text-lg font-bold truncate">{u.company || u.name}</div>
                            <div className="text-xs text-gray-500">Cliente desde {year}</div>
                        </div>
                    </div>
                    {isDeleted ? (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1"
                            style={{ background: 'rgba(220,38,38,0.15)', color: '#DC2626' }}>
                            Eliminado
                        </span>
                    ) : (
                        <div className="flex flex-col items-end gap-1">
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1" style={{ background: u.active ? 'rgba(16,185,129,0.15)' : 'rgba(156,163,175,0.15)', color: u.active ? '#10B981' : '#9CA3AF' }}>
                                <span className="w-1.5 h-1.5 rounded-full" style={{ background: u.active ? '#10B981' : '#9CA3AF' }} />
                                {u.active ? 'Activo' : 'Inactivo'}
                            </span>
                            {u.locked_at && (
                                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-1"
                                    style={{ background: 'rgba(220,38,38,0.15)', color: '#DC2626' }} data-testid={`client-locked-${u.id}`}>
                                    Cuenta bloqueada
                                </span>
                            )}
                        </div>
                    )}
                </div>
                <div className="space-y-2 text-sm border-t pt-4 mb-4" style={{ borderColor: 'var(--border)' }}>
                    {u.industry && (
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                            <Briefcase className="w-3.5 h-3.5" /> {u.industry}
                        </div>
                    )}
                    {(u.city || u.address) && (
                        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                            <MapPin className="w-3.5 h-3.5" />
                            <span className="truncate">{[u.city, u.state, u.country].filter(Boolean).join(', ') || u.address}</span>
                        </div>
                    )}
                </div>
                <span className="text-sm font-bold inline-flex items-center gap-1 hover:underline" style={{ color: VIOLET }}>
                    Ver detalles <ChevronRight className="w-3.5 h-3.5" />
                </span>
            </Link>
            {isDeleted && (
                <div className="absolute top-14 right-3 flex flex-col gap-1.5 items-end">
                    <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRestore?.(u); }}
                        data-testid={`restore-client-${u.id}`}
                        className="text-xs font-bold px-2.5 py-1 border-2 border-emerald-600 text-emerald-700 bg-white dark:bg-black hover:bg-emerald-600 hover:text-white transition-colors">
                        Restaurar
                    </button>
                    <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); onAskHardDelete?.(u); }}
                        data-testid={`hard-delete-client-${u.id}`}
                        title="Eliminar definitivamente (permanente)"
                        className="text-xs font-bold px-2.5 py-1 text-white bg-red-800 hover:bg-red-900 transition-colors">
                        Eliminar def.
                    </button>
                </div>
            )}
        </div>
    );
}

function ClientList({ items, onRestore, onAskHardDelete }) {
    return (
        <div className="border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border)' }}>
            <table className="w-full text-sm">
                <thead>
                    <tr className="border-b" style={{ borderColor: 'var(--border)' }}>
                        <th className="text-left px-5 py-3 font-bold text-xs uppercase tracking-wider">Empresa</th>
                        <th className="text-left px-5 py-3 font-bold text-xs uppercase tracking-wider">Industria</th>
                        <th className="text-left px-5 py-3 font-bold text-xs uppercase tracking-wider">Ubicación</th>
                        <th className="text-left px-5 py-3 font-bold text-xs uppercase tracking-wider">Estado</th>
                        <th className="text-right px-5 py-3 font-bold text-xs uppercase tracking-wider">Acciones</th>
                    </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: 'var(--border)' }}>
                    {items.map((u) => (
                        <tr key={u.id} className={`hover:bg-black/[0.02] dark:hover:bg-white/5 ${u.deleted ? 'opacity-60' : ''}`}>
                            <td className="px-5 py-4">
                                <Link to={`/admin/clients/${u.id}`} className="font-bold hover:underline">{u.company || u.name}</Link>
                                <div className="text-xs text-gray-500">{u.email}</div>
                            </td>
                            <td className="px-5 py-4 text-gray-600 dark:text-gray-400">{u.industry || '—'}</td>
                            <td className="px-5 py-4 text-gray-600 dark:text-gray-400">{[u.city, u.state].filter(Boolean).join(', ') || '—'}</td>
                            <td className="px-5 py-4">
                                {u.deleted ? (
                                    <span className="text-xs font-bold text-red-600">● Eliminado</span>
                                ) : (
                                    <span className="text-xs font-bold" style={{ color: u.active ? '#10B981' : '#9CA3AF' }}>
                                        ● {u.active ? 'Activo' : 'Inactivo'}
                                    </span>
                                )}
                            </td>
                            <td className="px-5 py-4 text-right">
                                {u.deleted && (
                                    <div className="inline-flex gap-1.5 justify-end">
                                        <button onClick={() => onRestore?.(u)}
                                            data-testid={`restore-client-row-${u.id}`}
                                            className="text-xs font-bold px-3 py-1 border-2 border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white transition-colors">
                                            Restaurar
                                        </button>
                                        <button onClick={() => onAskHardDelete?.(u)}
                                            data-testid={`hard-delete-client-row-${u.id}`}
                                            title="Eliminar definitivamente (permanente)"
                                            className="text-xs font-bold px-3 py-1 text-white bg-red-800 hover:bg-red-900 transition-colors">
                                            Eliminar def.
                                        </button>
                                    </div>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/**
 * Iter 44 · Hard-delete confirmation modal for a soft-deleted client/provider.
 * Requires typing the exact company/name label to confirm. Backend rejects
 * with 400 if the user is not already soft-deleted, so this modal is only
 * ever surfaced from the "Ver eliminados" view.
 */
function ConfirmHardDeleteClientModal({ user, onClose, onConfirmed }) {
    const label = user?.company || user?.trade_name || user?.name || '';
    const roleLabel = user?.role === 'provider' ? 'proveedor' : 'cliente';
    const [confirmText, setConfirmText] = useState('');
    const [busy, setBusy] = useState(false);
    const confirmMatches = confirmText.trim() === label.trim() && label.length > 0;

    const doHardDelete = async () => {
        if (!confirmMatches) return;
        setBusy(true);
        try {
            const _r = await api.delete(`/admin/users/${user.id}/purge?confirmar=BORRAR-TODO`);
            const _det = _r?.data?.eliminado || {};
            const _partes = Object.entries(_det).filter(([, v]) => v > 0)
              .map(([k, v]) => `${v} ${k.replace(/_/g, ' ')}`);
            toast.success(
              `${roleLabel === 'proveedor' ? 'Proveedor' : 'Cliente'} eliminado por completo` +
              (_partes.length ? ` · se libero: ${_partes.join(', ')}` : ''));
            onConfirmed();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Error al eliminar permanentemente'));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={busy ? undefined : onClose}
            data-testid="confirm-hard-delete-client-modal">
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto"
                style={{ background: 'var(--card-bg)', border: '2px solid #991B1B' }}>
                <div className="flex items-center justify-between px-6 py-4 border-b"
                    style={{ borderColor: '#991B1B', background: 'rgba(153,27,27,0.08)' }}>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center" style={{ background: 'rgba(153,27,27,0.2)' }}>
                            <AlertTriangle className="w-5 h-5 text-red-700" />
                        </div>
                        <div>
                            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-red-700">Acción irreversible</div>
                            <h2 className="font-display text-xl font-black">Eliminar {roleLabel} permanentemente</h2>
                        </div>
                    </div>
                </div>
                <div className="p-6 space-y-4">
                    <p className="text-sm text-gray-800 dark:text-gray-200">
                        Estás a punto de <strong>borrar de la base de datos</strong> a:
                    </p>
                    <div className="border-l-4 pl-4 py-2 bg-red-50 dark:bg-red-950/30" style={{ borderColor: '#991B1B' }}>
                        <div className="font-display text-lg font-black">{label}</div>
                        {user.email && <div className="text-xs text-gray-500">{user.email}</div>}
                    </div>
                    <div className="border p-3 text-xs" style={{ borderColor: '#F59E0B', background: 'rgba(245,158,11,0.08)' }}>
                        <div className="font-bold text-amber-700 dark:text-amber-500 mb-1 inline-flex items-center gap-1">
                            <AlertTriangle className="w-3.5 h-3.5" /> Sin restauración posible
                        </div>
                        <div className="text-gray-700 dark:text-gray-300">
                            A diferencia del soft-delete anterior, esta acción es <strong className="text-red-700">definitiva</strong>.
                            Sus auditorías, distintivos, hallazgos e historial <strong>se conservan intactos</strong> pero
                            quedarán huérfanos (sin usuario vinculado) — consistente con la política de Iter 43.
                        </div>
                    </div>
                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold mb-2 block text-gray-500">
                            Para confirmar, escribe el nombre exactamente: <span className="text-red-700">{label}</span>
                        </label>
                        <input type="text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)}
                            data-testid="confirm-hard-delete-client-input"
                            placeholder={label} autoFocus
                            className="w-full px-3 py-2 border-2 outline-none font-mono text-sm"
                            style={{
                                borderColor: confirmMatches ? '#10B981' : 'var(--border)',
                                background: 'var(--card-bg)',
                            }} />
                    </div>
                </div>
                <div className="px-6 py-4 border-t flex gap-2 justify-end" style={{ borderColor: 'var(--border)' }}>
                    <button type="button" onClick={onClose} disabled={busy}
                        className="px-5 py-2 border font-bold hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-50"
                        style={{ borderColor: 'var(--border)' }}>
                        Cancelar
                    </button>
                    <button type="button" onClick={doHardDelete} disabled={!confirmMatches || busy}
                        data-testid="confirm-hard-delete-client-submit"
                        className="px-6 py-2 text-white font-bold inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{ background: '#991B1B' }}>
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        Eliminar para siempre
                    </button>
                </div>
            </div>
        </div>
    );
}


function NewClientModal({ onClose, onCreated, initialData }) {
    const [form, setForm] = useState({
        name: '', email: '', password: '', company: '', role: 'client',
        industry: '', phone: '', contact_person: '',
        address: '', city: '', state: '', country: 'México',
        latitude: '', longitude: '', notes: '',
        trade_name: '', rfc: '', fiscal_address: '',
        legal_representative: '', general_manager: '',
        property_status: '', work_shifts: [],
        desired_certification_ids: [],
        desired_distintivo_ids: [],
        ...(initialData || {}),  // Iter 45 · prefill from onboarding data
    });
    const [rubros, setRubros] = useState([]);
    const [distintivos, setDistintivos] = useState([]);
    const [geoLoading, setGeoLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const set = (k, v) => setForm({ ...form, [k]: v });

    // Fetch rubros dynamically from admin panel data
    useEffect(() => {
        api.get('/rubros').then((r) => setRubros(r.data || [])).catch(() => setRubros([]));
        // Iter 51 Fase D · fetch Distintivos (bundles) instead of raw standards
        api.get('/public/distintivos').then((r) => setDistintivos(r.data || [])).catch(() => setDistintivos([]));
    }, []);

    const rubroOptions = rubros.length > 0
        ? rubros.map((r) => r.name).sort((a, b) => a.localeCompare(b))
        : INDUSTRIES;

    const RFC_RE = /^[A-ZÑ&]{3,4}\d{6}[A-Z\d]{2,3}$/i;

    const captureGeo = () => {
        if (!navigator.geolocation) {
            toast.error('Tu navegador no soporta geolocalización');
            return;
        }
        setGeoLoading(true);
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setForm((f) => ({
                    ...f,
                    latitude: pos.coords.latitude.toFixed(6),
                    longitude: pos.coords.longitude.toFixed(6),
                }));
                toast.success('Ubicación capturada con éxito');
                setGeoLoading(false);
            },
            (err) => {
                toast.error(`No se pudo obtener ubicación: ${err.message}`);
                setGeoLoading(false);
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    const submit = async (e) => {
        e.preventDefault();
        const rfc = (form.rfc || '').trim().toUpperCase();
        if (rfc && !RFC_RE.test(rfc)) {
            toast.error('RFC inválido (persona moral 12 caracteres o física 13)');
            return;
        }
        setSubmitting(true);
        try {
            const payload = { ...form, rfc: rfc || null };
            // Normalize empties to null
            for (const k of ['phone', 'address', 'city', 'state', 'country',
                'contact_person', 'notes', 'trade_name', 'fiscal_address',
                'legal_representative', 'general_manager', 'property_status']) {
                if (payload[k] === '') payload[k] = null;
            }
            // work_shifts: send array (or null if empty)
            if (!Array.isArray(payload.work_shifts) || payload.work_shifts.length === 0) {
                payload.work_shifts = null;
            }
            // desired_certification_ids (legacy): always send array (even if empty)
            if (!Array.isArray(payload.desired_certification_ids)) {
                payload.desired_certification_ids = [];
            }
            // Iter 51 Fase D · new distintivo bundle picker
            if (!Array.isArray(payload.desired_distintivo_ids)) {
                payload.desired_distintivo_ids = [];
            }
            payload.latitude = payload.latitude ? parseFloat(payload.latitude) : null;
            payload.longitude = payload.longitude ? parseFloat(payload.longitude) : null;
            await api.post('/admin/users', payload);
            toast.success(form.role === 'provider' ? 'Proveedor creado con éxito' : 'Cliente creado con éxito');
            onCreated(); onClose();
        } catch (err) {
            toast.error(apiErrorMessage(err, 'Error al crear'));
        } finally { setSubmitting(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
                className="w-full max-w-2xl flex flex-col max-h-[calc(100vh-2rem)]" data-testid="new-client-modal"
                style={{ background: 'var(--card-bg)', border: '1px solid var(--border)' }}>
                <div className="flex items-center justify-between px-6 py-4 border-b shrink-0" style={{ borderColor: 'var(--border)' }}>
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.2em] font-bold mb-1" style={{ color: VIOLET }}>Alta de cliente</div>
                        <h2 className="font-display text-xl font-black">Nuevo Cliente</h2>
                    </div>
                    <button type="button" onClick={onClose} className="p-2 hover:bg-black/5 dark:hover:bg-white/10"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-6 space-y-5 overflow-y-auto flex-1 min-h-0">
                    {/* Tipo */}
                    <div className="grid grid-cols-2 gap-2 border p-1" style={{ borderColor: 'var(--border)' }}>
                        <button type="button" onClick={() => set('role', 'client')}
                            className={`py-2 font-bold text-sm inline-flex items-center justify-center gap-2 ${form.role === 'client' ? 'text-white' : 'text-gray-500'}`}
                            style={form.role === 'client' ? { background: '#1F8FB8' } : {}}>
                            <Building2 className="w-4 h-4" /> Cliente principal
                        </button>
                        <button type="button" onClick={() => set('role', 'provider')}
                            className={`py-2 font-bold text-sm inline-flex items-center justify-center gap-2 ${form.role === 'provider' ? 'text-white' : 'text-gray-500'}`}
                            style={form.role === 'provider' ? { background: '#E08B3C' } : {}}>
                            <Briefcase className="w-4 h-4" /> Proveedor
                        </button>
                    </div>

                    {/* Datos generales */}
                    <Section title="Datos generales">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Input label="Nombre comercial *" v={form.trade_name} on={(v) => set('trade_name', v)} req placeholder="Marca o nombre comercial" />
                            <Input label="Razón social *" v={form.company} on={(v) => set('company', v)} req placeholder="Ej. Hotel Camino Real S.A. de C.V." />
                            <Input label="RFC" v={form.rfc} on={(v) => set('rfc', v.toUpperCase())} placeholder="Persona moral (12) o física (13)" maxLength={13} />
                            <Input label="Teléfono" v={form.phone} on={(v) => set('phone', v)} placeholder="(+52) ..." />
                            <Input label="Correo electrónico *" type="email" v={form.email} on={(v) => set('email', v)} req placeholder="contacto@empresa.com" />
                            <Input label="Contraseña inicial *" type="text" v={form.password} on={(v) => set('password', v)} req placeholder="Auto generada" />
                            <SelectField label="Rubro / Giro *" v={form.industry} on={(v) => set('industry', v)} req options={rubroOptions} placeholder="Selecciona el rubro" />
                            <MultiCertField label="Distintivos deseados" values={form.desired_distintivo_ids} onChange={(arr) => set('desired_distintivo_ids', arr)} distintivos={distintivos} />
                            <SelectField label="Situación del predio" v={form.property_status} on={(v) => set('property_status', v)}
                                options={['Propio', 'Arrendado', 'En comodato', 'Prestado', 'Otro']}
                                placeholder="Selecciona situación" />
                        </div>
                    </Section>

                    {/* Direcciones */}
                    <Section title="Direcciones">
                        <Input label="Dirección (operativa)" v={form.address} on={(v) => set('address', v)} placeholder="Calle, número, colonia (planta / sucursal)" />
                        <Input label="Dirección fiscal" v={form.fiscal_address} on={(v) => set('fiscal_address', v)} placeholder="Domicilio fiscal (si difiere de la operativa)" />
                    </Section>

                    {/* Representación */}
                    <Section title="Representación y operación">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Input label="Persona de contacto *" v={form.name} on={(v) => set('name', v)} req placeholder="Nombre del responsable" />
                            <Input label="Representante legal" v={form.legal_representative} on={(v) => set('legal_representative', v)} placeholder="Nombre del representante" />
                            <Input label="Gerente general" v={form.general_manager} on={(v) => set('general_manager', v)} placeholder="Nombre del gerente" />
                        </div>
                    </Section>

                    {/* Turnos */}
                    <Section title="Turnos laborales">
                        <WorkShiftsField
                            value={form.work_shifts}
                            onChange={(v) => set('work_shifts', v)}
                            testIdPrefix="new-client-shift"
                        />
                    </Section>

                    {/* Ubicación */}
                    <Section title="Ciudad y geolocalización">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <Input label="Ciudad" v={form.city} on={(v) => set('city', v)} placeholder="Cancún" />
                            <Input label="Estado" v={form.state} on={(v) => set('state', v)} placeholder="Quintana Roo" />
                            <Input label="País" v={form.country} on={(v) => set('country', v)} placeholder="México" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                            <Input label="Latitud" v={form.latitude} on={(v) => set('latitude', v)} placeholder="21.1619" />
                            <Input label="Longitud" v={form.longitude} on={(v) => set('longitude', v)} placeholder="-86.8515" />
                            <button type="button" onClick={captureGeo} disabled={geoLoading} data-testid="capture-geo"
                                className="text-white px-4 py-3 font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
                                style={{ background: VIOLET }}>
                                {geoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPinned className="w-4 h-4" />}
                                {geoLoading ? 'Obteniendo...' : 'Capturar GPS'}
                            </button>
                        </div>
                        {form.latitude && form.longitude && (
                            <div className="text-xs text-gray-500">
                                📍 Coordenadas: {form.latitude}, {form.longitude} •{' '}
                                <a href={`https://www.google.com/maps?q=${form.latitude},${form.longitude}`} target="_blank" rel="noreferrer" className="underline" style={{ color: VIOLET }}>
                                    Ver en Google Maps ↗
                                </a>
                            </div>
                        )}
                    </Section>

                    {/* Notas */}
                    <div>
                        <label className="text-xs uppercase tracking-wider font-bold mb-2 block text-gray-500">Notas internas</label>
                        <textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)}
                            className="w-full px-3 py-2 border outline-none resize-none"
                            style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }} />
                    </div>
                </div>
                <div className="px-6 py-4 border-t flex gap-2 justify-end shrink-0" style={{ borderColor: 'var(--border)' }}>
                    <button type="button" onClick={onClose} className="px-5 py-2 border hover:bg-black/5 dark:hover:bg-white/10" style={{ borderColor: 'var(--border)' }}>Cancelar</button>
                    <button type="submit" disabled={submitting} data-testid="submit-new-client"
                        className="text-white px-6 py-2 font-bold inline-flex items-center gap-2 disabled:opacity-50"
                        style={{ background: VIOLET }}>
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        Dar de alta
                    </button>
                </div>
            </form>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="space-y-3">
            <div className="text-xs uppercase tracking-[0.2em] font-bold" style={{ color: VIOLET }}>{title}</div>
            {children}
        </div>
    );
}

function Input({ label, v, on, req, type = 'text', placeholder }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-wider font-bold mb-2 block text-gray-500">{label}</label>
            <input
                type={type} value={v} onChange={(e) => on(e.target.value)} required={req} placeholder={placeholder}
                className="w-full px-3 py-2 border outline-none"
                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}
            />
        </div>
    );
}

function SelectField({ label, v, on, req, options = [], placeholder }) {
    return (
        <div>
            <label className="text-xs uppercase tracking-wider font-bold mb-2 block text-gray-500">{label}</label>
            <select
                value={v} onChange={(e) => on(e.target.value)} required={req}
                data-testid="industry-select"
                className="w-full px-3 py-2 border outline-none"
                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)', color: 'var(--foreground)' }}
            >
                <option value="">{placeholder || 'Selecciona una opción'}</option>
                {options.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                ))}
            </select>
        </div>
    );
}


function MultiCertField({ label, values = [], onChange, distintivos = [] }) {
    const [open, setOpen] = useState(false);
    const selected = distintivos.filter((s) => values.includes(s.id));
    const selectableDistintivos = distintivos.filter((s) => s.is_active !== false || values.includes(s.id));
    const toggle = (id) => {
        onChange(values.includes(id) ? values.filter((x) => x !== id) : [...values, id]);
    };
    return (
        <div className="relative">
            <label className="text-xs uppercase tracking-wider font-bold mb-2 block text-gray-500">{label}</label>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                data-testid="multi-cert-toggle"
                className="w-full px-3 py-2 border outline-none text-left flex items-center justify-between"
                style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}
            >
                <span className="truncate text-sm">
                    {selected.length === 0 ? <span className="text-gray-400">Ninguno seleccionado</span>
                        : selected.length === 1 ? selected[0].code
                        : `${selected.length} seleccionados`}
                </span>
                <span className="text-gray-400 text-xs">{open ? '▲' : '▼'}</span>
            </button>
            {selected.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                    {selected.map((s) => {
                        const inactive = s.is_active === false;
                        return (
                            <span key={s.id} className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5" style={{ background: inactive ? 'rgba(220,38,38,0.15)' : '#7C6BEF22', color: inactive ? '#DC2626' : '#7C6BEF' }} title={inactive ? 'Distintivo desactivado del catálogo' : ''}>
                                {s.code}{inactive ? ' (inactivo)' : ''}
                                <button type="button" onClick={() => toggle(s.id)} className="hover:opacity-70" data-testid={`multi-cert-chip-remove-${s.id}`}>
                                    <X className="w-2.5 h-2.5" />
                                </button>
                            </span>
                        );
                    })}
                </div>
            )}
            {open && (
                <div className="absolute z-10 mt-1 w-full max-h-64 overflow-y-auto border shadow-lg" style={{ borderColor: 'var(--border)', background: 'var(--card-bg)' }}>
                    {selectableDistintivos.length === 0 ? (
                        <div className="p-3 text-xs text-gray-400 italic">Cargando…</div>
                    ) : (
                        selectableDistintivos.map((s) => {
                            const inactive = s.is_active === false;
                            const comingSoon = !!s.coming_soon;
                            const disabled = inactive || comingSoon;
                            return (
                                <label key={s.id}
                                    className={`flex items-center gap-2 px-3 py-2 text-sm border-b last:border-b-0 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-black/5 dark:hover:bg-white/10'}`}
                                    style={{ borderColor: 'var(--border)', opacity: disabled ? 0.55 : 1 }}
                                    title={comingSoon ? 'Estará disponible próximamente' : (inactive ? 'Distintivo inactivo' : '')}
                                >
                                    <input type="checkbox" checked={values.includes(s.id)} disabled={disabled} onChange={() => !disabled && toggle(s.id)} data-testid={`multi-cert-check-${s.id}`} />
                                    <span className="font-bold">{s.code}</span>
                                    <span className="text-xs text-gray-500 truncate">{s.name}</span>
                                    {comingSoon && (
                                        <span className="text-[9px] uppercase tracking-wider font-bold ml-auto px-1.5 py-0.5" style={{ background: 'rgba(245,158,11,0.15)', color: '#B45309' }} data-testid={`coming-soon-badge-${s.code}`}>
                                            Próximamente
                                        </span>
                                    )}
                                    {inactive && !comingSoon && <span className="text-[9px] font-bold text-red-500 ml-auto">INACTIVO</span>}
                                </label>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
}
